import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Diagnóstico', href: '/diagnostico', highlight: true },
    { name: 'Curso', href: '/#curso' },
    { name: 'Libro', href: '/#libro' },
    { name: 'Testimonios', href: '/#testimonios' },
    { name: 'Contacto', href: '/contacto' }
  ];

  const handleNavClick = (e, href) => {
    setIsMenuOpen(false);
    
    // If it's a hash link (starts with /# or #)
    if (href.includes('#')) {
      e.preventDefault();
      const sectionId = href.split('#')[1];
      
      if (location.pathname === '/') {
        // Already on home page, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home page first, then scroll
        navigate('/');
        // Use setTimeout to wait for navigation to complete
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  return (
    <header className="fixed top-0 w-full bg-white/98 backdrop-blur-md z-50 border-b border-cream-dark/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="group flex items-center">
            <span className="text-xl lg:text-2xl font-light tracking-[0.25em] text-charcoal group-hover:text-golden transition-colors duration-500">
              KARI ROJAS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`nav-link relative px-4 py-2 text-xs lg:text-sm font-normal tracking-[0.15em] uppercase transition-all duration-300 ${
                  item.highlight 
                    ? 'text-golden hover:text-golden-dark' 
                    : 'text-charcoal/70 hover:text-charcoal'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="relative z-10">{item.name}</span>
                {/* Underline hover effect */}
                <span className="absolute bottom-1 left-4 right-4 h-px bg-golden scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100 nav-underline"></span>
              </Link>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-charcoal/70 hover:text-golden transition-colors duration-300"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <nav className="py-4 border-t border-cream-dark/30">
            <div className="flex flex-col space-y-1">
              {navigation.map((item, index) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`px-2 py-3 text-sm font-light tracking-[0.15em] uppercase transition-all duration-300 ${
                    item.highlight 
                      ? 'text-golden' 
                      : 'text-charcoal/70 hover:text-charcoal hover:pl-4'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
